// Renders a structured ads-account-audit sample (see ./sample-audit-reports) in
// Soku's editorial report style by mapping it onto the SAME typed block model
// the competitor-analysis reports use (see ./sample-report-html): masthead, stat
// band, sections of ledes / tables-with-pills / a bar chart / cards, and an
// accent conclusion. Reusing that renderer keeps both sample reports visually
// identical (fonts, charts, sortable tables, sticky nav, tooltips, reveal-on-
// scroll) instead of maintaining a second, plainer layout.
//
// The output is dropped into an <iframe srcDoc> on /free-ads-audit; all input is
// static, in-repo "dead data" (no user input).

import type { Breakdown, ReportStatus, SampleReport } from './sample-audit-reports'
import {
  buildReportHtml,
  type Cell,
  type CompetitorReport,
  type PillVariant,
} from './sample-report-html'

export function buildAdsAuditReportHtml(report: SampleReport): string {
  return buildReportHtml(toCompetitorReport(report))
}

// ── Status → pill variant ─────────────────────────────────────────────────────

function statusPill(status: ReportStatus): PillVariant {
  switch (status) {
    case 'Drawdown':
    case 'Critical':
    case 'Elevated':
    case 'Declining':
      return 'down'
    case 'Improving':
      return 'up'
    default:
      return 'neutral'
  }
}

// Parse a money/number-ish cell ("$8,420", "$10") into a sortable magnitude for
// the campaign-spend bar chart. Falls back to 0 for non-numeric cells.
function parseAmount(value: string): number {
  const m = value.replace(/[, ]/g, '').match(/-?\d+(?:\.\d+)?/)
  return m ? parseFloat(m[0]) : 0
}

// ── Mapping ───────────────────────────────────────────────────────────────────

type Block = CompetitorReport['sections'][number]['blocks'][number]
type Section = CompetitorReport['sections'][number]

// A Breakdown (campaign guardrails or a device/geo/search-term dimension table)
// → a table block: name + its cells + a trailing status pill.
function breakdownToTable(breakdown: Breakdown): Block {
  return {
    type: 'table',
    columns: [...breakdown.columns],
    rows: breakdown.rows.map(row => ({
      highlight: row.status === 'Drawdown' || row.status === 'Critical',
      cells: [
        row.name,
        ...row.cells,
        { text: row.status, pill: statusPill(row.status) } as Cell,
      ],
    })),
  }
}

function priorityPill(priority: 'P0' | 'P1' | 'P2'): PillVariant {
  if (priority === 'P0') return 'down'
  if (priority === 'P1') return 'accent'
  return 'neutral'
}

function toCompetitorReport(report: SampleReport): CompetitorReport {
  const comparisonTable: Block = {
    type: 'table',
    columns: ['Metric', 'Current', 'Prev', '7d avg', '14d avg', 'Target', 'Status'],
    rows: report.comparison.map(row => ({
      highlight: row.status === 'Drawdown' || row.status === 'Critical',
      cells: [
        row.metric,
        row.current,
        row.previous,
        row.avg7,
        row.avg14,
        row.target,
        { text: row.status, pill: statusPill(row.status) } as Cell,
      ],
    })),
  }

  // Spend-by-campaign bar chart from the breakdown's first numeric column.
  const spendBars: Block = {
    type: 'bars',
    title: 'Spend by campaign',
    sub: 'Where the budget is actually going this period',
    bars: report.breakdown.rows.map(row => ({
      label: row.name,
      value: parseAmount(row.cells[0] ?? '0'),
      display: row.cells[0] ?? '',
      accent: row.status === 'Drawdown' || row.status === 'Critical',
    })),
  }

  // Narrative findings → a card grid (like the competitor positioning cards).
  const findingCards: Block = {
    type: 'cards',
    cards: report.sections.map(section => ({
      title: section.title,
      body: [...(section.paras ?? []), ...(section.bullets ?? [])].join(' '),
    })),
  }

  // 30-day plan → a table when priorities/impact are authored, else a card grid.
  const planHasTable = report.plan.some(p => p.priority || p.impact)
  const planBlock: Block = planHasTable
    ? {
        type: 'table',
        sortable: false,
        columns: ['Priority', 'Week', 'Action', 'Expected impact'],
        rows: report.plan.map(item => ({
          highlight: item.priority === 'P0',
          cells: [
            item.priority
              ? ({ text: item.priority, pill: priorityPill(item.priority) } as Cell)
              : '—',
            item.week,
            `${item.title}. ${item.body}`,
            item.impact ?? '—',
          ],
        })),
      }
    : {
        type: 'cards',
        cards: report.plan.map(item => ({
          title: `${item.week} — ${item.title}`,
          body: item.body,
        })),
      }

  const sections: Section[] = [
    {
      id: 'overview',
      nav: 'Overview',
      title: 'KPI Overview',
      blocks: [
        { type: 'lede', text: report.tldr },
        {
          type: 'prose',
          paragraphs: [{ heading: report.alert.title, body: report.alert.body }],
        },
        comparisonTable,
      ],
    },
  ]

  // Optional weekly trend chart (e.g. blended ROAS across the window).
  if (report.trend) {
    sections.push({
      id: 'trend',
      nav: 'Trend',
      title: 'Performance trend',
      blocks: [
        {
          type: 'trend',
          title: report.trend.title,
          sub: report.trend.sub,
          points: report.trend.points,
        },
      ],
    })
  }

  sections.push({
    id: 'guardrails',
    nav: 'Campaigns',
    title: report.breakdown.title,
    blocks: [
      ...(report.breakdown.lede
        ? [{ type: 'lede', text: report.breakdown.lede } as Block]
        : []),
      breakdownToTable(report.breakdown),
      spendBars,
    ],
  })

  // Optional extra dimension tables — device / geo / wasted-spend / ad-copy.
  for (const [i, dim] of (report.dimensions ?? []).entries()) {
    sections.push({
      id: `dimension-${i + 1}`,
      nav: navLabel(dim.title),
      title: dim.title,
      blocks: [
        ...(dim.lede ? [{ type: 'lede', text: dim.lede } as Block] : []),
        breakdownToTable(dim),
      ],
    })
  }

  sections.push({
    id: 'findings',
    nav: 'Findings',
    title: 'Diagnostic findings',
    blocks: [
      {
        type: 'lede',
        text: 'Where spend is leaking and where structure, creative, and measurement are quietly costing performance.',
      },
      findingCards,
    ],
  })

  sections.push({
    id: 'plan',
    nav: 'Plan',
    title: 'Prioritized 30-day action plan',
    blocks: [planBlock],
  })

  sections.push(methodologySection(report))

  return {
    title: `${report.brand} — Ads Account Audit`,
    subtitle: report.meta,
    navBrand: report.brand,
    meta: `Health score <strong>${report.healthScore}/100</strong> &nbsp;·&nbsp; Window <strong>Last 30 days</strong> &nbsp;·&nbsp; Analysis-only access — write-capable workflows enabled separately`,
    stats: report.kpis.map(kpi => ({
      label: kpi.label,
      value: kpi.value,
      note: kpi.delta,
    })),
    sections,
    conclusion: {
      title: 'Projected impact',
      paragraphs: [{ body: report.projection }],
    },
  }
}

// Transparency section — what the audit reads and how flags fire. Static across
// reports (an account audit runs on the customer's own connected data, so unlike
// the competitor reports there are no third-party estimates to caveat).
function methodologySection(report: SampleReport): Section {
  return {
    id: 'methodology',
    nav: 'Data',
    title: 'Methodology & data sources',
    blocks: [
      {
        type: 'table',
        sortable: false,
        columns: ['Item', 'Detail'],
        rows: [
          { cells: ['Data sources', report.meta] },
          {
            cells: [
              'Metrics',
              'Spend, conversions, revenue, ROAS / CPA pulled directly from the connected ad platforms — measured account data, not modeled third-party estimates.',
            ],
          },
          {
            cells: [
              'Window',
              'Trailing 30 days, with 7-day and 14-day blended averages for trend context.',
            ],
          },
          {
            cells: [
              'Attribution',
              'Platform-reported conversions, reconciled against GA4 / store revenue where those sources are connected.',
            ],
          },
          {
            cells: [
              'Guardrails',
              'Drawdown / Critical flags fire when a metric breaches its target band or trips a rolling 7-day guardrail (e.g. ROAS below the drawdown line).',
            ],
          },
          {
            cells: [
              'Access',
              'Analysis-only access for this audit. Write-capable workflows must be enabled separately and then follow the approval or automation rules configured for the workspace.',
            ],
          },
        ],
      },
    ],
  }
}

// Short label for the sticky nav — the first meaningful word only, so the nav
// stays a compact ~2 rows (matching the competitor report) instead of wrapping.
function navLabel(title: string): string {
  const first =
    title.split(/[\s/&,(:—–-]+/).filter(Boolean)[0] ?? title.trim()
  return first.length > 14 ? `${first.slice(0, 13)}…` : first
}
